import { HOUR, MINUTE } from '../helpers';

export type IncidentComment = {
  status: string;
  timestamp: number | string;
  text: string;
};

export type Severity =
  | 'ok'
  | 'info'
  | 'maintenance'
  | 'disrupted'
  | 'down'
  | 'unknown';

export type AffectedComponent = {
  name: string;
  severity: Severity;
};

type IncidentType = 'ok' | 'maintenance' | 'incident' | 'unknown';

const orderedSeverities = [
  'ok',
  'maintenance',
  'unknown',
  'disrupted',
  'down',
] as const;

function maxSeverity(...severities: Severity[]): Severity {
  return severities.reduce((acc: Severity, v) => {
    if (orderedSeverities.indexOf(v) > orderedSeverities.indexOf(acc)) {
      return v;
    }
    return acc;
  }, 'ok');
}

export type IncidentDTO = {
  id: string;
  title: string;
  type: IncidentType;
  start: number | string;
  end: number | string;
  affected?: AffectedComponent[];
  details?: IncidentComment[];
};

export class Incident {
  id: string;
  title: string;
  type: IncidentType;
  start: Date;
  end: Date;
  affected: AffectedComponent[];
  details: IncidentComment[];

  constructor(data?: IncidentDTO | Incident) {
    if (data) {
      this.start = new Date(data.start);
      this.end = new Date(data.end);
      this.id = data.id;
      this.title = data.title;
      this.type = data.type;
      this.affected = data.affected ?? [];
      this.details = data.details ?? [];
    } else {
      this.start = new Date(0);
      this.end = new Date(0);
      this.id = '';
      this.title = '';
      this.type = 'ok';
      this.details = [];
      this.affected = [];
    }
  }

  isFinished(): boolean {
    return !isNaN(this.end.getTime()) && this.end > this.start;
  }
  severity(): Severity {
    if (this.isFinished()) return 'ok';
    switch (this.type) {
      case 'ok':
      case 'maintenance':
        return this.type;
      default:
        return maxSeverity(...this.affected.map((ac) => ac.severity));
    }
    return 'unknown';
  }
  isSameMonth(d: Date): boolean {
    return this.end.getUTCMonth() === d.getUTCMonth();
  }
  getHoursSinceStart(d: Date): number {
    return Math.floor((d.getTime() - this.start.getTime()) / HOUR);
  }
  getHoursSinceEnd(d: Date): number {
    let timeDiffEnd: number = 0;
    if (this.isFinished()) {
      timeDiffEnd = Math.floor((d.getTime() - this.end.getTime()) / HOUR);
    }
    return timeDiffEnd;
  }
  presentXHoursAgo(xHours: number): boolean {
    //check if it happened in last X minutes
    const currentDate: Date = new Date();
    const timeDiffStart: number = this.getHoursSinceStart(currentDate);
    const timeDiffEnd: number = this.getHoursSinceEnd(currentDate);
    return timeDiffStart < xHours || timeDiffEnd < xHours;
  }
  getMinutesOfDowntimeAt(d: Date): number {
    let startDate: Date = new Date(this.start);
    let incDowntime: number = 0;
    if (this.type === 'ok') {
      return incDowntime;
    }
    if (d.getUTCMonth() !== startDate.getUTCMonth()) {
      // inc started a long time ago, we want to count uptime only from 1 day of current month
      startDate = new Date(d.getFullYear(), d.getUTCMonth(), 1);
    }
    if (this.isFinished()) {
      // end time - end of incident if it's already closed
      incDowntime = Math.floor(
        (this.end.getTime() - startDate.getTime()) / MINUTE
      );
    } else {
      // ongoing incidents, set end time to current time
      incDowntime = Math.floor((d.getTime() - startDate.getTime()) / MINUTE);
    }
    return incDowntime;
  }

  merge(newIncident: Incident) {
    if (this.id === '') {
      this.start = newIncident.start;
      this.id = newIncident.id;
      this.title = newIncident.title;
      this.type = newIncident.type;
      this.affected = newIncident.affected;
      this.details = newIncident.details;
    } else {
      this.details.unshift(...newIncident.details);
      switch (newIncident.type) {
        case 'ok':
        case 'maintenance':
          break;
        case 'incident':
        case 'unknown':
          this.affected.push(
            ...newIncident.affected.filter(
              (ac) => !this.affected.find((nac) => nac.name === ac.name)
            )
          );
          this.affected = this.affected.map((ac) => {
            const newSeverity =
              newIncident.affected.find((nac) => nac.name === ac.name)
                ?.severity ?? 'unknown';
            return {
              name: ac.name,
              severity: maxSeverity(ac.severity, newSeverity),
            };
          });
          break;
        default:
          throw new Error(`Unexpected incident type '${newIncident.type}'`);
      }
      const affectedComponenets: string[] = this.affected.map((ac) => {
        return ac.name;
      });
      this.title = 'Incident with ' + affectedComponenets.join(', ');
    }
  }

  serialize(): IncidentDTO {
    return {
      id: this.id,
      title: this.title,
      type: this.type,
      start: this.start.getTime(),
      end: this.end.getTime(),
      affected: this.affected,
      details: this.details,
    };
  }
}
